import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon, CopyIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Callout, Card, GraphQLErrorMessage, Heading3, Paragraph } from '@treams/treams-ui';

import ApiTokenEmailForm from '../components/ApiTokenEmailForm';
import ApiTokenTable from '../components/ApiTokenTable';

const Container = styled(Box)`
	word-wrap: break-word;
	max-width: 60%;
`;

type Props = {
	type: string;
};

const ApiTokens: FC<Props> = ({ type }) => {
	const { t } = useTranslation();

	const [tempToken, setTempToken] = useState<graphql.GenerateApiTokenResponse | undefined>(
		undefined,
	);
	const [hasCopied, setHasCopied] = useState(false);

	const tokensQuery = graphql.useApiTokensQuery({ variables: { type } });
	const emailQuery = graphql.useApiTokenNotificationEmailQuery({
		variables: { type },
	});
	const [update, { loading }] = graphql.useUpdateApiTokenNotificationEmailMutation();

	const tokens = tokensQuery.data?.apiTokenDescriptions ?? [];
	const email = emailQuery.data?.apiTokenNotificationEmail.email ?? '';

	const [remove, { error: removeError }] = graphql.useRevokeApiTokenMutation();
	const [add, { error: addError }] = graphql.useGenerateApiTokenMutation();

	const error = tokensQuery.error ?? removeError ?? addError ?? undefined;

	const removeToken = async (id: string) => {
		await remove({ variables: { id, type } });
		await tokensQuery.refetch();
	};

	const addToken = async () => {
		const response = await add({ variables: { type } });
		await tokensQuery.refetch();
		setTempToken(response.data?.generateApiToken);
	};

	const copy = async () => {
		if (tempToken) {
			await navigator.clipboard.writeText(tempToken.value);
			setHasCopied(true);
		}
	};

	const close = () => {
		setTempToken(undefined);
		setHasCopied(false);
	};

	return (
		<Card>
			<Box flexGrow={1} flexShrink={1}>
				<Heading3>{t('apitokens.title')}</Heading3>

				{emailQuery.data && (
					<ApiTokenEmailForm
						email={email}
						loading={loading}
						onSubmit={(email) => update({ variables: { type, email } })}
					/>
				)}

				{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

				{!tokensQuery.error && (
					<ApiTokenTable tokens={tokens} email={email} onRemove={removeToken} onAdd={addToken} />
				)}

				{tempToken && (
					<Callout color="green" mt={3}>
						<Container>
							<Heading3>{t('apitokens.key')}</Heading3>
							<Paragraph>{tempToken.value}</Paragraph>
						</Container>
						<Flex flexGrow={1} justifyContent="flex-end">
							<Flex alignItems="center" mr={3}>
								<Button
									type="secondary"
									icon={<CopyIcon />}
									title={t(`apitokens.${hasCopied ? 'copied' : 'copy'}`)}
									onClick={copy}
								/>
							</Flex>

							<Flex alignItems="center">
								<Button type="secondary" title={t('common.close')} onClick={close} />
							</Flex>
						</Flex>
					</Callout>
				)}

				{tokens.length > 0 && tokens.length < 2 && (
					<Flex justifyContent="flex-end" alignItems="center" mt={3}>
						<Button icon={<AddIcon />} title={t('apitokens.add')} onClick={addToken} />
					</Flex>
				)}
			</Box>
		</Card>
	);
};

export default ApiTokens;
